<script>
import { mapActions, mapGetters } from 'vuex';
import Vue from 'vue';
import axios from 'axios';
import Donut from 'vue-css-donut-chart';
import EsLoader from '@/components/EsLoader.vue';
import EsMatch from '@/components/EsMatch.vue';
import NotificationManager from '@/mixins/NotificationManager';
import FieldLabels from '@/mixins/FieldLabels';
import StateIdUtils from '@/mixins/StateIdUtils';
import 'vue-css-donut-chart/dist/vcdonut.css';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
  faBan,
  faCheck,
  faCaretDown,
  faCaretUp,
} from '@fortawesome/pro-regular-svg-icons';

library.add(
  faAngleDoubleRight,
  faBan,
  faCheck,
  faCaretDown,
  faCaretUp,
);

Vue.use(Donut);

export default {
  mixins: [
    NotificationManager,
    FieldLabels,
    StateIdUtils,
  ],
  props: {
    loadedDistrictSchools: {
      type: Object,
    },
    lowerBound: {
      type: String,
      required: false,
    },
    matchPercent: {
      type: Number,
      required: false,
    },
    nearMatch: {
      type: Object,
      required: true,
    },
    upperBound: {
      type: String,
      required: false,
    },
    personType: {
      type: String,
    },
  },
  components: {
    EsLoader,
    EsMatch,
  },
  data() {
    return {
      districts: [],
      isExpanded: false,
      isMatch: false,
      isNoMatch: false,
      loading: null,
      nearMatchDistrictCode: null,
      nearMatchTranId: null,
      schools: [],
      showModal: false,
    };
  },
  async created() {
    await this.onLoad();
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters({
      hasDistrictSchoolPermission: 'hasDistrictSchoolPermission',
    }),
    ...mapGetters('lookups', [
      'activeGenders',
      'activeRaces',
    ]),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    decisionButtonLabel() {
      return this.isNoMatch ? this.properties['BUTTON_LABEL-CREATE_NEW'] : this.properties['BUTTON_LABEL-ASSIGN_EXISTING'];
    },
    potentialNearMatches() {
      if (this.nearMatch.dmMatchTransDetail.response.potentialMatches) {
        return this.nearMatch.dmMatchTransDetail.response.potentialMatches.length;
      }
      return null;
    },
    roundMatchPercent() {
      const percentages = this.nearMatch.dmMatchTransDetail.response.potentialMatches
        .map(match => match.percent);
      return Math.max(...percentages);
    },
    stateBatch() {
      return this.nearMatch.dmMatchTransDetail.response.request.matchType === 'DM_ALL_STUDENTS';
    },
    targetRecord() {
      return this.nearMatch.dmMatchTransDetail.response.request.target;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadGenders',
      'loadPersonTypes',
      'loadRaces',
    ]),
    ...mapActions([
      'getDistrict',
      'getSchool',
    ]),
    buildDistrictSchoolApi(code) {
      const districtCode = code.substring(0, code.indexOf(':'));
      const schoolCode = code.substring(code.indexOf(':') + 1, code.length);
      return `districts/${districtCode}/schools/${schoolCode}`;
    },
    async createMatch(idForAssign, districtCode) {
      try {
        this.modalClose();
        await axios.put(`directmatch/transactions/${this.nearMatch.id}`,
          { command: 'MATCH', matchIdForAssign: idForAssign, districtCode });
        this.showNotification('success', [`${this.properties['SYSTEM_LABEL-NEARMATCH']} Resolved: Match`]);
        this.$emit('refresh');
      } catch (error) {
        this.handleMatchError(error);
      }
    },
    decisionAction() {
      if (this.isNoMatch) {
        this.notMatch();
      } else if (this.isMatch) {
        this.createMatch(this.nearMatchTranId, this.nearMatchDistrictCode);
      }
    },
    districtCodeFromSchoolCode(schoolCode) {
      return schoolCode.substring(0, 3);
    },
    formatDate(date) {
      const year = date.substring(0, 4);
      const month = date.substring(4, 6);
      const day = date.substring(6);
      return `${month}/${day}/${year}`;
    },
    formatSsn(ssn) {
      if (ssn) {
        if (!this.hasDistrictSchoolPermission(this.matchPermission('DRMCH:PRPRF:SSN:VIEW'))) {
          return this.maskSsn(ssn);
        }
        const numbers = ssn.replace(/[^0-9]/g, '');
        if (numbers.length === 9) {
          return `${numbers.substr(0, 3)}-${numbers.substr(3, 2)}-${numbers.substr(5, 4)}`;
        }
      }
      return null;
    },
    getDistrictName(matchedObject) {
      const object = this.getDistrictSchoolObject(matchedObject);
      return object.districtLocation.name;
    },
    getDistrictSchoolObject(matchedObject) {
      const { districtCode, schoolCode } = matchedObject;
      const key = `${districtCode}:${schoolCode}`;
      return this.loadedDistrictSchools[key];
    },
    getSchoolName(matchedObject) {
      const object = this.getDistrictSchoolObject(matchedObject);
      return object.location.name;
    },
    handleMatchError(error) {
      const message = `${error.response.status === 500 ? 'Network Error' : error.response.data.message}`;
      this.showNotification('danger', [message]);
    },
    async loadDistrictsAndSchools() {
      try {
        this.loading = true;
        // get unique set of district and school codes for each near match
        const districtSchoolCodes = new Set(
          this.nearMatch.dmMatchTransDetail.response.potentialMatches.map(
            potentialMatch => `${potentialMatch.matchedObject.districtCode}:${potentialMatch.matchedObject.schoolCode}`,
          ),
        );
        // check to see if these code are already in the Set
        [...districtSchoolCodes].forEach((code) => {
          if (this.loadedDistrictSchools[code]) districtSchoolCodes.delete(code);
        });
        // build promises to get district and school info
        const DistrictSchoolResponses = await Promise.all(
          [...districtSchoolCodes].map(code => axios.get(this.buildDistrictSchoolApi(code))),
        );
        // use the response from above and push them into prop
        DistrictSchoolResponses.forEach((districtSchool) => {
          const { districtCode, code } = districtSchool.data;
          const key = `${districtCode}:${code}`;
          this.loadedDistrictSchools[key] = districtSchool.data;
        });
      } catch (e) {
        // what do we want to show on error?
      } finally {
        this.loading = false;
      }
    },
    mapGender(genderCode) {
      const genders = this.activeGenders.find(gender => gender.code === genderCode);
      if (genders) {
        return genders.description;
      }
      return '\u00A0';
    },
    mapRace(raceCode) {
      const races = this.activeRaces.find(race => race.code === raceCode);
      if (races) {
        return races.description;
      }
      return '\u00A0';
    },
    maskSsn(ssn) {
      const maskedSSN = ssn.split('-').map(part => part.replace(/\d/g, '#')).join('-');
      return `${maskedSSN.substr(0, 3)}-${maskedSSN.substr(3, 2)}-${maskedSSN.substr(5, 4)}`;
    },
    matchPermission(permission) {
      return {
        permission,
        districtCode: this.nearMatch.districtCode,
        schoolCode: this.nearMatch.schoolCode,
      };
    },
    modalClose() {
      this.showModal = false;
      this.isMatch = false;
      this.isNoMatch = false;
    },
    modalOpenForMatch(idForAssign, districtCode) {
      this.showModal = true;
      this.isMatch = true;
      this.nearMatchTranId = idForAssign;
      this.nearMatchDistrictCode = districtCode;
    },
    modalOpenForNoMatch() {
      this.showModal = true;
      this.isNoMatch = true;
    },
    nearMatchedObject(value) {
      if (this.stateBatch) {
        return value.matchedObject;
      }
      return value.matchedObject.uncompressedTherecord;
    },
    async notMatch() {
      try {
        this.modalClose();
        await axios.put(`directmatch/transactions/${this.nearMatch.id}`,
          { command: 'NO_MATCH', districtCode: this.nearMatch.districtCode });
        this.showNotification('success', [`${this.properties['SYSTEM_LABEL-NEARMATCH']} Resolved: No Match`]);
        this.$emit('refresh');
      } catch (error) {
        this.handleMatchError(error);
      }
    },
    async onLoad() {
      try {
        await Promise.all([
          this.loadGenders(),
          this.loadRaces(),
          this.loadPersonTypes(),
        ]);
      } catch (error) {
        this.apiMsg = `${error.response.status === 500
          ? 'Network Error'
          : error.response.data.message}`;
        this.messages.push(this.apiMsg);
        this.loadingError = true;
        this.hasError = true;
        this.showMessage();
      }
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded && this.stateBatch) {
        this.loadDistrictsAndSchools();
      }
    },
  },
  filters: {
    getDescripton(value, list) {
      if (value) {
        const item = list.find(g => g.code === value);
        if (item) {
          return item.description;
        }
      }
      return value;
    },
    getName(value, list) {
      if (value) {
        const item = list.find(g => g.code === value);
        if (item) {
          return item.name;
        }
      }
      return '\u00A0';
    },
    orPlaceHolder(value) {
      if (value) {
        return value;
      }
      return '\u00A0';
    },
  },
};
</script>

<template>
  <div class="document">
    <div
      v-if="showModal"
      class="modal is-active"
    >
      <div class="modal-background"/>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title has-text-weight-bold">
            Confirm Decision
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="modalClose()"
          />
        </header>
        <section
          v-if="isNoMatch || isMatch"
          class="modal-card-body"
        >
          <div class="modal-body has-text-left">
            Are you sure you want to make a "{{ decisionButtonLabel }}"
            decision for the submission record?
          </div>
          <div class="is-flex flex-justify--end flex-align--center">
            <a @click="modalClose()">
              Cancel
            </a>
            <button
              class="button is-primary margin-left"
              @click="decisionAction"
            >
              Yes, Select {{ decisionButtonLabel }}
            </button>
          </div>
        </section>
      </div>
    </div>
    <div class="card-container">
      <div class="card--heading is-flex">
        <div
          v-if="stateBatch"
          class="card-cell is-flex flex-direction--column"
        >
          <div
            class="is-flex flex-direction--column has-text-grey"
            style="height: 100%; justify-content: space-around"
          >
            <img src="@/assets/state.png">
          </div>
          <div class="has-text-grey" style="font-size: 10px">
            <div>
              STATE
            </div>
            <div>
              BATCH
            </div>
          </div>
        </div>
        <div class="card-cell is-flex flex-direction--column overflow--ellipsis">
          <div
            v-if="stateBatch"
            class="card-cell--title"
          >
            Program Record
          </div>
          <div
            v-else
            class="card-cell--title"
          >
            Submission Record
          </div>
          <div class="has-text-left is-flex">
            <div
              class="card-cell--body is-size-4 overflow--ellipsis"
              style="font-size: 1.25rem"
            >
              <span v-if="targetRecord.fullName">
                {{ targetRecord.fullName }}
              </span>
              <span v-else>-</span>
            </div>
          </div>
          <div
            v-if="!stateBatch"
            class="is-flex flex-direction--column has-text-left"
            style="font-size: 14px; height: 100%; justify-content: space-around"
          >
            <div>
              <div v-if="nearMatch.district">
                <span class="has-text-weight-bold">
                  {{ nearMatch.district.location.name }}
                </span>
                <span class="is-italic has-text-grey-light has-text-weight-light">
                  ({{ nearMatch.district.code }})
                </span>
              </div>
              <div v-else>-</div>
            </div>
            <div>
              <div v-if="nearMatch.school">
                <span class="has-text-weight-bold">
                  {{ nearMatch.school.location.name }}
                </span>
                <span class="is-italic has-text-grey-light has-text-weight-light">
                  ({{ nearMatch.school.code }})
                </span>
              </div>
              <div v-else>-</div>
            </div>
          </div>
        </div>
        <div
          class="card-cell is-flex border--left flex-align--center margin-left--auto"
          style="min-width: 200px"
        >
          <div
            class="is-flex flex-direction--column has-text-right has-text-grey"
            style="
              padding-right: 0.5rem;
              font-size: 12px;
              height: 100%;
              justify-content: space-around"
          >
            <div>
              {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:DATE_OF_BIRTH`) }}:
            </div>
            <div>
              {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:GRADE_CODE`) }}:
            </div>
            <div v-if="!stateBatch">
              {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:LOCAL_STUDENT_ID`) }}:
            </div>
            <div v-if="stateBatch">
              Client Index ID:
            </div>
          </div>
          <div
            class="is-flex flex-direction--column has-text-left has-text-weight-bold"
            style="font-size: 16px; height: 100%; justify-content: space-around"
          >
            <div>
              <span>
                 {{ formatDate(targetRecord.dateOfBirth) || '-' }}
              </span>
            </div>
            <div>
              <span>
                {{ targetRecord.gradeCode || '-' }}
              </span>
            </div>
            <div v-if="!stateBatch">
              <span>
                {{ targetRecord.localcode || '-' }}
              </span>
            </div>
            <div v-if="stateBatch">
              <span>
                {{ targetRecord.uniqueidentifer || '-' }}
              </span>
            </div>
          </div>
        </div>
        <div
          class="card-cell is-flex border--left flex-align--center"
          style="min-width: 250px"
        >
          <div
            class="is-flex flex-direction--column has-text-right has-text-grey"
            style="
              padding-right: 0.5rem;
              font-size: 12px;
              height: 100%;
              justify-content: space-around
            "
          >
            <div>
              {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:RACE_ETHNIC_CODE`) }}:
            </div>
            <div>
              {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:GENDER_CODE`) }}:
            </div>
            <div>
              {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:SOCIAL_SECURITY_NUMBER`) }}:
            </div>
          </div>
          <div
            class="is-flex flex-direction--column has-text-left has-text-weight-bold"
            style="font-size: 16px; height: 100%; justify-content: space-around"
          >
            <div>
              <span>
                {{
                  targetRecord.raceEthnicCode | getDescripton(activeRaces)
                }}
              </span>
            </div>
            <div>
              <span>
                {{
                  targetRecord.genderCode | getDescripton(activeGenders)
                }}
              </span>
            </div>
            <div>
              <span v-if="targetRecord.ssn">
                {{ formatSsn(targetRecord.ssn) | orPlaceHolder }}
              </span>
              <span v-else>-</span>
            </div>
          </div>
        </div>
<!-- DM PERSON TYPE: NQD-2366 -->
        <div
          class="card-cell is-flex border--left flex-align--center"
          style="min-width: 250px"
        >
          <div
            class="is-flex flex-direction--column has-text-right has-text-grey"
            style="
              padding-right: 0.5rem;
              font-size: 12px;
              height: 100%;
              justify-content: space-around"
          >
            <div>
              Person Type:
            </div>
            <div>
              Match Type:
            </div>
          </div>
          <div
            class="is-flex flex-direction--column has-text-left has-text-weight-bold"
            style="font-size: 16px; height: 100%; justify-content: space-around"
          >
            <div>
              <span>
                {{ targetRecord.uncompressedTherecord.personType
                | getName(userPersonTypes) | orPlaceHolder}}
              </span>
            </div>
            <div>
              <span>
                {{ nearMatch.program.description }}
              </span>
            </div>
          </div>
        </div>
<!-- DM PERSON TYPE: NQD-2366 -->
        <div
          class="card-cell is-flex border--left flex-direction--row flex-align--center"
          style="min-width: 150px"
        >
          <div class="padding-right">
            <vc-donut
              :size="60"
              :sections="[
              { value: roundMatchPercent - 1, color: '#215E97' },
              { value: 1, color: '#FFFFFF' },
              { value: 99 - roundMatchPercent, color: '#C8C8C8'},
              { value: 1, color: '#FFFFFF' },]"
            >
              <h1 class="percent-label">{{ roundMatchPercent }}%</h1>
            </vc-donut>
          </div>
          <div class="has-text-left has-text-grey">
            <div>
              Max
            </div>
            <div>
              Match
            </div>
            <div>
              Score
            </div>
          </div>
        </div>
        <div
          class="card-cell is-flex border--left flex-direction--row flex-align--center"
          style="min-width: 150px"
        >
          <div class="has-text-weight-bold is-size-3 padding-right">
            {{ potentialNearMatches }}
          </div>
          <div class="has-text-grey">
            <div>
              Potential
            </div>
            <div>
              Matches
            </div>
          </div>
        </div>
        <div
          class="card-cell
            is-flex
            border--left
            flex-direction--row
            flex-align--center
            flex-justify--end"
          style="min-width: 105px"
        >
          <a>
            <font-awesome-icon style="font-size: 1.5rem;"
              class="is-primary"
              v-if="!isExpanded"
              :icon="['far', 'caret-down']"
              @click="toggleExpand"
              title="View Details"
              size="lg"
            />
            <font-awesome-icon style="font-size: 1.5rem;"
              class="is-primary"
              v-else
              @click="toggleExpand"
              :icon="['far', 'caret-up']"
              title="Hide Details"
              size="lg"
            />
          </a>
          <a
            v-if="hasDistrictSchoolPermission(matchPermission('DRMCH:MATCH:RESOLVENM:NOMATCH'))"
            class="margin-left"
          >
            <font-awesome-icon style="font-size: 1.2rem;"
              class="is-primary"
              :icon="['far', 'ban']"
              :title="`${properties['BUTTON_LABEL-CREATE_NEW']}`"
              @click="modalOpenForNoMatch()"
              size="lg"
            />
          </a>
          <router-link
            :to="{
              name: 'directMatchCompareMatches',
              params: { nearMatchId: nearMatch.id }
            }"
            class="margin-left"
          >
            <font-awesome-icon style="font-size: 1.7rem;"
              class="is-primary"
              :icon="['far', 'angle-double-right']"
              :title="`Compare ${properties['SYSTEM_LABEL-NEARMATCH']}(s/es)`"
              size="lg"
            />
          </router-link>
        </div>
      </div>
      <div
        v-if="isExpanded"
        class="card--body"
      >
        <div
          v-if="stateBatch"
          class="has-text-left border--bottom has-text-weight-bold"
        >
          Potential Matches to Person ID
        </div>
        <div
          v-else
          class="has-text-left border--bottom has-text-weight-bold"
        >
          Potential Matches to Program Index Record
        </div>
        <table class="table is-fullwidth is-hoverable">
          <thead>
            <tr>
              <th></th>
              <th>Person Name</th>
              <th>Location</th>
              <th>{{ getFieldLabel(`PER:${personType}:DIRECTMATCH:DATE_OF_BIRTH`) }}</th>
              <th>{{ getFieldLabel(`PER:${personType}:DIRECTMATCH:GRADE_CODE`) }}</th>
              <th>{{ getFieldLabel(`PER:${personType}:DIRECTMATCH:LOCAL_STUDENT_ID`) }}</th>
              <th>{{ getFieldLabel(`PER:${personType}:DIRECTMATCH:RACE_ETHNIC_CODE`) }}</th>
              <th>{{ getFieldLabel(`PER:${personType}:DIRECTMATCH:GENDER_CODE`) }}</th>
              <th>{{ getFieldLabel(`PER:${personType}:DIRECTMATCH:SOCIAL_SECURITY_NUMBER`) }}</th>
              <th>Match Score</th>
              <th class="has-text-right">Actions</th>
            </tr>
          </thead>
          <tbody v-if="loading">
            <tr>
              <td
                colspan="100%"
                style="text-align: center"
              >
                <es-loader style="margin: 2rem"/>
              </td>
            </tr>
          </tbody>
          <tbody v-else-if="nearMatch.dmMatchTransDetail.response.potentialMatches !== null">
            <tr
              v-for="potentialMatch in nearMatch.dmMatchTransDetail.response.potentialMatches"
              :key="potentialMatch.id"
            >
              <td class="padding--none">
                <es-match/>
              </td>
              <td>
                <div>
                  <span v-if="potentialMatch.matchedObject.fullName">
                    {{ potentialMatch.matchedObject.fullName }}
                  </span>
                  <span v-else>-</span>
                </div>
                <div>
                  <span
                    v-if="potentialMatch.id"
                    class="is-italic has-text-grey-light"
                  >
                    <span v-if="stateBatch">
                      {{ formatStateID(potentialMatch.id) }}
                    </span>
                    <span v-else>
                      {{ potentialMatch.id }}
                    </span>
                  </span>
                  <span v-else>-</span>
                </div>
              </td>
              <td>
                <div v-if="stateBatch">
                  <div>
                    <span>
                      {{ getDistrictName(potentialMatch.matchedObject) }}
                    </span>
                    <span class="is-italic has-text-grey-light">
                      ({{ potentialMatch.matchedObject.districtCode }})
                    </span>
                  </div>
                  <div>
                    <span>
                      {{ getSchoolName(potentialMatch.matchedObject) }}
                    </span>
                    <span class="is-italic has-text-grey-light">
                      ({{ potentialMatch.matchedObject.schoolCode }})
                    </span>
                  </div>
                </div>
              </td>
              <td>
                <span v-if="nearMatchedObject(potentialMatch).dateOfBirth">
                  {{ formatDate(nearMatchedObject(potentialMatch).dateOfBirth) }}
                </span>
              </td>
              <td>
                <span v-if="nearMatchedObject(potentialMatch).gradeCode">
                  {{ nearMatchedObject(potentialMatch).gradeCode }}
                </span>
              </td>
              <td>
                <span v-if="nearMatchedObject(potentialMatch).localcode">
                  {{ nearMatchedObject(potentialMatch).localcode }}
                </span>
              </td>
              <td>
                <span v-if="nearMatchedObject(potentialMatch).raceEthnicCode">
                  {{ mapRace(nearMatchedObject(potentialMatch).raceEthnicCode) }}
                </span>
              </td>
              <td>
                <span v-if="nearMatchedObject(potentialMatch).genderCode">
                  {{ mapGender(nearMatchedObject(potentialMatch).genderCode) }}
                </span>
              </td>
              <td>
                <template v-if="stateBatch">
                  <span v-if="potentialMatch.matchedObject.ssn">
                    {{ formatSsn(potentialMatch.matchedObject.ssn) }}
                  </span>
                </template>
                <template v-else>
                  <span
                    v-if="
                      potentialMatch.matchedObject.uncompressedTherecord.socialSecurityNumber"
                  >
                    {{
                      formatSsn(
                        potentialMatch.matchedObject.uncompressedTherecord.socialSecurityNumber
                      )
                    }}
                  </span>
                </template>
              </td>
              <td>
                <div class="is-flex justify-content--start">
                  <vc-donut
                    :size="50"
                    :sections="[
                    { value: potentialMatch.percent - 1, color: '#215E97' },
                    { value: 1, color: '#FFFFFF' },
                    { value: 99 - potentialMatch.percent, color: '#C8C8C8'},
                    { value: 1, color: '#FFFFFF' }]"
                  >
                    <h2 class="percent-label is-size-6">
                      {{ potentialMatch.percent }}%
                    </h2>
                  </vc-donut>
                </div>
              </td>
              <td class="has-text-right">
                <a v-if="hasDistrictSchoolPermission(
                    matchPermission('DRMCH:MATCH:RESOLVENM:MATCHDEC')
                  )"
                >
                  <font-awesome-icon style="font-size: 1.4rem;"
                    class="is-primary"
                    :icon="['far', 'check']"
                    size="lg"
                    @click="modalOpenForMatch(
                      potentialMatch.id, nearMatchedObject(potentialMatch).districtCode
                    )"
                    :title="`${properties['BUTTON_LABEL-ASSIGN_EXISTING']}`"
                  />
                </a>
                <router-link
                  :to="{
                    name: 'directMatchCompareMatches',
                    params: { nearMatchId: nearMatch.id }
                  }"
                  class="margin-left"
                >
                  <font-awesome-icon style="font-size: 1.7rem;"
                    class="is-primary"
                    :icon="['far', 'angle-double-right']"
                    size="lg"
                    :title="`Compare ${properties['SYSTEM_LABEL-NEARMATCH']}(s/es)`"
                  />
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
  .border--bottom
    border-bottom: 1px solid #ccc
  .border--left
    border-left: 1px solid #ccc
  .card--body
    padding: 1.5rem 4rem
    max-width: 100%
  .card-cell
    padding: 0 1rem
    margin-bottom: 0.5rem
  .card-cell--title
    padding: 0.5rem 0
    color: #999
    font-size: 12px
    text-align: left
  .card-container
    padding-top: 0.5rem
  .card--heading
    min-height:  70px
  .document
    margin-bottom: 1rem
  .margin-left
    margin-left: 1rem
  .margin-left--auto
    margin-left: auto
  .modal-body has-text-left
    margin-bottom: 1rem
    display: flex
    justify-content: flex-start
  .modal-card-title
    display: flex
  .overflow--ellipsis
    overflow: hidden
    text-overflow: ellipsis
  .padding--none
    padding: 0px !important
  .padding-right
    padding-right: 1rem
  .percent-label
    font-style: bold
    color: black
    font-size: 1.1rem
    font-weight: 800
    padding: .25rem 0 .25rem .15rem
</style>
